import { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, MenuItem, TextField, Typography } from "@mui/material";
import StickyNote2RoundedIcon from "@mui/icons-material/StickyNote2Rounded";
import AppDialog from "../General/Dialog/AppDialog";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { createWorkspaceTask, updateWorkspaceTask } from "../../services/WorkspaceService";
import { getUsers } from "../../services/UserService";
import { personName } from "./workspaceUtils";

const MAX_LEGAL_FILES = 200;

function parseLegalFileCodes(raw) {
  return raw
    .split(/[\n\r,\t;]+/)
    .map((code) => code.trim())
    .filter(Boolean)
    .filter((code, index, arr) => arr.indexOf(code) === index);
}

export default function CreateTaskDialog({ open, onClose, onCreated, task }) {
  const { showToast } = useToast();
  const { auth } = useAuth();
  const currentUserId = auth?.user?.id ?? "";
  const isEditing = !!task;

  const [text, setText] = useState("");
  const [codesRaw, setCodesRaw] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setText(task?.text || "");
      setCodesRaw((task?.legal_file_codes || []).join("\n"));
      setAssigneeId(task?.assigned_to?.id ?? currentUserId);
    }
  }, [open, task, currentUserId]);

  useEffect(() => {
    if (!open || isEditing) return;
    let active = true;
    getUsers()
      .then((res) => {
        if (!active) return;
        const list = res.data?.data ?? res.data;
        setUsers(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (active) setUsers([]);
      });
    return () => {
      active = false;
    };
  }, [open, isEditing]);

  const userOptions = useMemo(() => {
    const list = [...users];
    if (currentUserId && !list.some((u) => u.id === currentUserId)) {
      list.unshift({ id: currentUserId, name: auth?.user?.name || "Yo", lastname: "" });
    }
    return list.sort((a, b) => {
      if (a.id === currentUserId) return -1;
      if (b.id === currentUserId) return 1;
      return personName(a).localeCompare(personName(b));
    });
  }, [users, currentUserId, auth]);

  const codes = parseLegalFileCodes(codesRaw);
  const exceeded = codes.length > MAX_LEGAL_FILES;
  const visibleCodes = exceeded ? codes.slice(0, MAX_LEGAL_FILES) : codes;

  const handleClose = () => {
    if (saving) return;
    setText("");
    setCodesRaw("");
    setAssigneeId("");
    onClose();
  };

  const handleSave = async () => {
    if (!text.trim()) {
      showToast("Debe ingresar el texto de la tarea.", "error");
      return;
    }

    if (exceeded) {
      showToast(
        `Ingresó ${codes.length} expedientes. El máximo permitido es ${MAX_LEGAL_FILES}.`,
        "error",
      );
      return;
    }

    try {
      setSaving(true);
      const payload = { text: text.trim(), legal_file_codes: visibleCodes };
      if (!isEditing && assigneeId) {
        payload.assigned_to = assigneeId;
      }
      const res = isEditing
        ? await updateWorkspaceTask(task.id, payload)
        : await createWorkspaceTask(payload);

      if (res.data?.status === "success" || res.data) {
        showToast(
          res.data?.message || (isEditing ? "Tarea actualizada correctamente." : "Tarea creada correctamente."),
          "success",
        );
        handleClose();
        await onCreated?.();
      }
    } catch (err) {
      showToast(
        err.response?.data?.message || (isEditing ? "Error al actualizar la tarea." : "Error al crear la tarea."),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      title={isEditing ? "Editar tarea" : "Nueva tarea"}
      description={
        isEditing
          ? "Modifique el texto de la tarea y, opcionalmente, la lista de expedientes asociados."
          : "Cree una tarea, asígnela a un responsable y, opcionalmente, la lista de expedientes asociados."
      }
      icon={<StickyNote2RoundedIcon />}
      actions={
        <>
          <Button
            onClick={handleClose}
            sx={{ borderRadius: 2, textTransform: "none" }}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none", px: 3 }}
          >
            {saving ? "Guardando..." : isEditing ? "Guardar cambios" : "Crear tarea"}
          </Button>
        </>
      }
    >
      <TextField
        label="Texto de la tarea"
        placeholder="Describa lo que se necesita hacer..."
        multiline
        minRows={4}
        fullWidth
        value={text}
        onChange={(e) => setText(e.target.value)}
        sx={{ mt: 2 }}
      />

      {!isEditing && (
        <TextField
          select
          label="Asignar a"
          fullWidth
          value={assigneeId}
          onChange={(e) => setAssigneeId(e.target.value)}
          helperText="Solo el responsable (y los administradores) verá esta tarea."
          sx={{ mt: 2.5 }}
        >
          {userOptions.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {u.id === currentUserId ? `${personName(u) || "Yo"} (yo)` : personName(u)}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Box sx={{ mt: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
          Expedientes asociados
        </Typography>
        <Chip
          size="small"
          label={`${Math.min(codes.length, MAX_LEGAL_FILES)}/${MAX_LEGAL_FILES}`}
          color={exceeded ? "error" : "default"}
          sx={{ fontWeight: 800, borderRadius: 999 }}
        />
      </Box>

      <TextField
        placeholder={`Pegue los códigos de expediente (uno por línea, o separados por coma/tabulación)...`}
        multiline
        minRows={6}
        fullWidth
        value={codesRaw}
        onChange={(e) => setCodesRaw(e.target.value)}
        error={exceeded}
        helperText={
          exceeded
            ? `Se detectaron ${codes.length} códigos. Solo se guardarán los primeros ${MAX_LEGAL_FILES}.`
            : "Puede pegar directamente desde Excel o un archivo de texto."
        }
        sx={{ mt: 1.5, "& textarea": { fontFamily: "monospace", fontSize: 13 } }}
      />
    </AppDialog>
  );
}
